import { useEffect, useRef } from 'react';
import { useTheme } from '@vitness/theme';
import { useStore } from './store';
import { api } from './api';
import { useUiScale, isUiScale, DB_KEY, LEGACY_DB_KEY, type UiScale } from './useUiScale';
import { BASIS_DB_KEY, hydrateNetBasis, useNetBasis } from './useNetBasis';
import { RAILS_DB_KEY, hydrateDialRails, useDialRails } from './useDialRails';

/**
 * D1 is the source of truth for preferences; localStorage is just a cache so
 * the first paint isn't unstyled.
 *
 * On boot we hydrate the ThemeProvider from whatever bootstrap returned, then
 * push any subsequent change back. The `hydrated` latch stops the hydration
 * itself from echoing straight back to the server.
 */
export function usePreferenceSync() {
  const settings = useStore(s => s.data?.settings);
  const { themeMode, accentName, density, surface, setMode, setAccent, setDensity, setSurface } = useTheme();
  const { scale, setScale, formFactor } = useUiScale();
  const { basis } = useNetBasis();
  const { rails } = useDialRails();

  const hydrated = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── DB → client, once ──
  useEffect(() => {
    if (hydrated.current || !settings) return;
    hydrated.current = true;

    const m = settings.theme_mode;
    const a = settings.theme_accent;
    const d = settings.theme_density;
    const s = settings.theme_surface;
    // This device class's own row, falling back to the pre-split shared key so
    // an existing preference isn't reset the first time a client updates.
    const own = Number(settings[DB_KEY[formFactor]]);
    const z = isUiScale(own) ? own : Number(settings[LEGACY_DB_KEY]);

    if (m && m !== themeMode) setMode(m as typeof themeMode);
    if (a && a !== accentName) setAccent(a as typeof accentName);
    if (d && d !== density) setDensity(d as typeof density);
    if (s && s !== surface) setSurface(s as typeof surface);
    if (isUiScale(z) && z !== scale) setScale(z as UiScale);
    // Unlike the mask, which stays on the device, the basis is which set of
    // numbers you are reasoning with — a phone and a laptop disagreeing about
    // that is worse than either default.
    hydrateNetBasis(settings[BASIS_DB_KEY] as 'accrual' | 'guaranteed' | undefined);
    // Not split per form factor, unlike the UI scale. The scale is split
    // because 125% means something different on a phone; whether you drive the
    // table by rail or by typing is the same preference everywhere, and the
    // request was explicitly "all views at any viewport".
    hydrateDialRails(settings[RAILS_DB_KEY]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  // ── client → DB, debounced ──
  useEffect(() => {
    if (!hydrated.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.putSettings({
        theme_mode: themeMode,
        theme_accent: accentName,
        theme_density: density,
        theme_surface: surface,
        // Only this device class's key. Writing a single shared `ui_scale` is
        // what made setting 125% on the laptop also zoom the phone to 125%.
        [DB_KEY[formFactor]]: String(scale),
        [BASIS_DB_KEY]: basis,
        [RAILS_DB_KEY]: rails ? 'on' : 'off',
      }).catch(() => { /* offline — localStorage still holds it */ });
    }, 400);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [themeMode, accentName, density, surface, scale, formFactor, basis, rails]);
}
